import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import {
  taxPortalService,
  TaxPortalOverview,
  TaxReturn,
  TaxReturnDetailResponse,
  TaxNotice,
  TaxNoticeDetailResponse,
  PendingResponse,
  ResponseHistoryItem,
  SubmitResponsePayload,
  TaxDemand,
  NoticeResponse,
  GenerateResponsePayload,
  SubmitNoticeResponsePayload,
  TaxDemandDetailResponse,
  RefineResponsePayload,
  RefinementAction,
  NoticeAnalysis,
  ParsedDocumentResponse,
  SubmittedResponsesResponse,
  SubmittedResponseDetail,
  WhatsNewResponse,
  WhatsNewCounts,
  whatsNewService,
  EprocSummary,
} from '@/services/taxPortal.service';

interface ApiError {
  response?: {
    data?: {
      error?: string;
      status?: string;
      message?: string;
      errors?: Record<string, unknown>;
    };
  };
  data?: {
    error?: string;
  };
  message: string;
}

interface TaxPortalState {
  overview: {
    data: TaxPortalOverview;
    loading: boolean;
    error: string | null;
  };
  eprocs: {
    list: TaxNotice[];
    calendar: TaxNotice[];
    selectedEproceeding: {
      loading: boolean;
      data: TaxNoticeDetailResponse;
      error: string | null;
    };
    loading: boolean;
    error: string | null;
    listPagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      totalRecords?: number;
    };
    calendarPagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      totalRecords?: number;
    };
  };
  taxReturns: {
    list: TaxReturn[];
    selectedReturn: {
      loading: boolean;
      data: TaxReturnDetailResponse | null;
      error: string | null;
    };
    loading: boolean;
    error: string | null;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  responses: {
    pendingList: PendingResponse[];
    history: ResponseHistoryItem[];
    loading: boolean;
    error: string | null;
    submitStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
  demands: {
    list: TaxDemand[];
    selectedDemand: {
      loading: boolean;
      data: TaxDemandDetailResponse;
      error: string | null;
    };
    loading: boolean;
    error: string | null;
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      totalRecords?: number;
    };
  };
  noticeResponse: {
    data: NoticeResponse | null;
    loading: boolean;
    submitting: boolean;
    success: boolean;
    error: string | null;
  };
  generatedResponse: {
    eprocId: string;
    noticeId: string;
    responseCategory: 'COMPLIANCE' | 'OBJECTION' | 'APPEAL';
    responseText: string;
    tokensUsed: number;
    loading: boolean;
    submitting: boolean;
    success: boolean;
    error: string | null;
  };
  showDetailsModal: {
    open: boolean;
    name: string;
  };
  aiRefinement: {
    isRefining: boolean;
    activeAction: RefinementAction | null;
    refinedText: string;
    fullResponse: string;
    tokensUsed: number;
    error: string | null;
  };
  noticeAnalysis: {
    data: NoticeAnalysis | null;
    noticeId: string | null;
    loading: boolean;
    error: string | null;
    // Streaming state
    status: string | null;
    streamingSections: Partial<NoticeAnalysis>;
    isStreaming: boolean;
  };
  prepareResponse: {
    isOpen: boolean;
    eprocId: string | null;
    noticeId: string | null;
    supportingDocuments: SupportingDocumentMeta[];
    documentUpload: {
      isUploading: boolean;
      error: string | null;
    };
  };
  submittedResponses: {
    isOpen: boolean;
    clientId: string | null;
    clientName: string | null;
    selectedNoticeId: string | null;
    data: SubmittedResponseDetail[];
    loading: boolean;
    error: string | null;
  };
  demandPrepareResponse: {
    isOpen: boolean;
    demandId: string | null;
    supportingDocuments: SupportingDocumentMeta[];
    documentUpload: { isUploading: boolean; error: string | null };
  };
  demandGeneratedResponse: {
    demandId: string;
    responseCategory: string;
    responseText: string;
    tokensUsed: number;
    loading: boolean;
    error: string | null;
  };
  demandAiRefinement: {
    isRefining: boolean;
    activeAction: string | null;
    refinedText: string;
    fullResponse: string;
    tokensUsed: number;
    error: string | null;
  };
  whatsNew: {
    data: WhatsNewResponse | null;
    loading: boolean;
    error: string | null;
  };
}

// Supporting document metadata (no extracted text - stored on server)
export interface SupportingDocumentMeta {
  id: string;
  fileName: string;
  fileType: 'pdf' | 'docx' | 'xlsx';
  characterCount: number;
}

const initialState: TaxPortalState = {
  overview: {
    data: {
      totalClients: 0,
      totalBalance: 0,
      demandsRaised: 0,
      ongoingProceedings: 0,
      refundsDue: 0,
      percentageDemandsRaised: 0,
      refundsIssued: 0,
      priorityTasks: {
        overdue: {
          pendingITRs: 0,
          overdueProceedings: 0,
        },
        upcoming: {
          pendingITRs: 0,
          upcomingProceedings: 0,
        },
      },
      noticeReport: {
        monthly: [],
        quarterly: [],
        daily: [],
      },
      itrStatus: {
        totalBusinesses: {
          filed: 0,
          notFiled: 0,
          eVerified: 0,
          pending: 0,
          processed: 0,
          defective: 0,
          rejected: 0,
          onHold: 0,
          deadline: '',
        },
        auditedBusinesses: {
          filed: 0,
          notFiled: 0,
          eVerified: 0,
          pending: 0,
          processed: 0,
          defective: 0,
          rejected: 0,
          onHold: 0,
          deadline: '',
        },
        nonAuditedBusinesses: {
          filed: 0,
          notFiled: 0,
          eVerified: 0,
          pending: 0,
          processed: 0,
          defective: 0,
          rejected: 0,
          onHold: 0,
          deadline: '',
        },
      },
      taxAuditProgress: {},
      taxCalendar: [],
      eprocSummary: {
        needsAction: 0,
        overdue: 0,
        dueSoon: 0,
        sectionBreakdown: [],
        statusBreakdown: [],
        last7Days: 0,
        last30Days: 0,
      },
    } as TaxPortalOverview,
    loading: false,
    error: null,
  },
  taxReturns: {
    list: [],
    selectedReturn: {
      loading: false,
      data: {} as TaxReturnDetailResponse,
      error: null,
    },
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  eprocs: {
    list: [],
    calendar: [],
    selectedEproceeding: {
      loading: false,
      data: {} as TaxNoticeDetailResponse,
      error: null,
    },
    loading: false,
    error: null,
    listPagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
    calendarPagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  responses: {
    pendingList: [],
    history: [],
    loading: false,
    error: null,
    submitStatus: 'idle',
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  demands: {
    list: [],
    selectedDemand: {
      loading: false,
      data: {} as TaxDemandDetailResponse,
      error: null,
    },
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      totalPages: 0,
    },
  },
  noticeResponse: {
    data: null,
    loading: false,
    submitting: false,
    success: false,
    error: null,
  },
  generatedResponse: {
    eprocId: '',
    noticeId: '',
    responseCategory: 'COMPLIANCE',
    responseText: '',
    tokensUsed: 0,
    loading: false,
    submitting: false,
    success: false,
    error: null,
  },
  showDetailsModal: {
    open: false,
    name: '',
  },
  aiRefinement: {
    isRefining: false,
    activeAction: null,
    refinedText: '',
    fullResponse: '',
    tokensUsed: 0,
    error: null,
  },
  noticeAnalysis: {
    data: null,
    noticeId: null,
    loading: false,
    error: null,
    status: null,
    streamingSections: {},
    isStreaming: false,
  },
  prepareResponse: {
    isOpen: false,
    eprocId: null,
    noticeId: null,
    supportingDocuments: [],
    documentUpload: {
      isUploading: false,
      error: null,
    },
  },
  submittedResponses: {
    isOpen: false,
    clientId: null,
    clientName: null,
    selectedNoticeId: null,
    data: [],
    loading: false,
    error: null,
  },
  demandPrepareResponse: {
    isOpen: false,
    demandId: null,
    supportingDocuments: [],
    documentUpload: { isUploading: false, error: null },
  },
  demandGeneratedResponse: {
    demandId: '',
    responseCategory: '',
    responseText: '',
    tokensUsed: 0,
    loading: false,
    error: null,
  },
  demandAiRefinement: {
    isRefining: false,
    activeAction: null,
    refinedText: '',
    fullResponse: '',
    tokensUsed: 0,
    error: null,
  },
  whatsNew: {
    data: null,
    loading: false,
    error: null,
  },
};

/**
 * Extracts error message from API error object
 * @param error - API error object
 * @returns Formatted error message
 */
const extractErrorMessage = (error: unknown): { error: string } => {
  const apiError = error as ApiError;
  return {
    error:
      apiError.response?.data?.message ||
      apiError.response?.data?.error ||
      apiError.data?.error ||
      apiError.message,
  };
};

export const fetchTaxPortalOverview = createAsyncThunk(
  'tax-portal/fetchOverview',
  async (_, { rejectWithValue }) => {
    try {
      return await taxPortalService.getOverview();
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchTaxReturns = createAsyncThunk(
  'tax-portal/fetchTaxReturns',
  async (
    {
      page = 1,
      limit = 10,
      search,
      assessmentYear,
      itrType,
      status,
      refundStatus,
      whatsNew,
    }: {
      page?: number;
      limit?: number;
      search?: string;
      assessmentYear?: string;
      itrType?: string;
      status?: string;
      refundStatus?: string;
      whatsNew?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.getTaxReturns({
        page,
        limit,
        search,
        assessmentYear,
        itrType,
        status,
        refundStatus,
        whatsNew,
      });
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchTaxReturnDetails = createAsyncThunk(
  'tax-portal/fetchTaxReturnDetails',
  async (returnId: string, { rejectWithValue }) => {
    try {
      const response = await taxPortalService.getTaxReturnDetails(returnId);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchTaxEproceedings = createAsyncThunk(
  'tax-portal/fetchTaxEproceedings',
  async (
    {
      page = 1,
      limit = 10,
      month,
      year,
      view = 'list',
      dueBy,
      search,
      assessmentYear,
      proceedingType,
      proceedingStatus,
      section,
      noticePAN,
      whatsNew,
    }: {
      page?: number;
      limit?: number;
      month?: string;
      year?: string;
      view?: string;
      dueBy?: string;
      search?: string;
      assessmentYear?: string;
      proceedingType?: string;
      proceedingStatus?: string;
      section?: string;
      noticePAN?: string;
      whatsNew?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.getTaxEproceedings({
        page,
        limit,
        month,
        year,
        dueBy,
        search,
        assessmentYear,
        proceedingType,
        proceedingStatus,
        section,
        noticePAN,
        whatsNew,
      });
      return { ...response, view };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchTaxEproceedingDetails = createAsyncThunk(
  'tax-portal/fetchTaxEproceedingDetails',
  async (eprocId: string, { rejectWithValue }) => {
    try {
      const response = await taxPortalService.getTaxEproceedingDetails(eprocId);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchPendingResponses = createAsyncThunk(
  'tax-portal/fetchPendingResponses',
  async ({ page = 1, limit = 10 }: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await taxPortalService.getPendingResponses(page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchResponseHistory = createAsyncThunk(
  'tax-portal/fetchResponseHistory',
  async ({ page = 1, limit = 10 }: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await taxPortalService.getResponseHistory(page, limit);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const submitResponse = createAsyncThunk(
  'tax-portal/submitResponse',
  async (
    { responseId, payload }: { responseId: string; payload: SubmitResponsePayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.submitResponse(responseId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchTaxDemands = createAsyncThunk(
  'tax-portal/fetchTaxDemands',
  async (
    {
      page = 1,
      limit = 10,
      search,
      assessmentYear,
      sectionCode,
      status,
      whatsNew,
      accruingInterest,
    }: {
      page?: number;
      limit?: number;
      search?: string;
      assessmentYear?: string;
      sectionCode?: string;
      status?: string;
      whatsNew?: boolean;
      accruingInterest?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.getTaxDemands({
        page,
        limit,
        search,
        assessmentYear,
        sectionCode,
        status,
        whatsNew,
        accruingInterest,
      });
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchTaxDemandsDetails = createAsyncThunk(
  'tax-portal/fetchTaxDemandsDetails',
  async (demandId: string, { rejectWithValue }) => {
    try {
      const response = await taxPortalService.getTaxDemandsDetails(demandId);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const fetchNoticeResponse = createAsyncThunk(
  'tax-portal/fetchNoticeResponse',
  async ({ eprocId, noticeId }: { eprocId: string; noticeId: string }, { rejectWithValue }) => {
    try {
      const response = await taxPortalService.getNoticeResponse(eprocId, noticeId);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const generateNoticeResponse = createAsyncThunk(
  'tax-portal/generateNoticeResponse',
  async (
    {
      eprocId,
      noticeId,
      payload,
    }: { eprocId: string; noticeId: string; payload: GenerateResponsePayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.generateNoticeResponse(eprocId, noticeId, payload);
      console.log('response', response);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const refineNoticeResponse = createAsyncThunk(
  'tax-portal/refineNoticeResponse',
  async (
    {
      eprocId,
      noticeId,
      payload,
    }: { eprocId: string; noticeId: string; payload: RefineResponsePayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.refineNoticeResponse(eprocId, noticeId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const submitNoticeResponse = createAsyncThunk(
  'tax-portal/submitNoticeResponse',
  async (
    {
      eprocId,
      noticeId,
      payload,
    }: { eprocId: string; noticeId: string; payload: SubmitNoticeResponsePayload },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.submitNoticeResponse(eprocId, noticeId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const analyzeNotice = createAsyncThunk(
  'tax-portal/analyzeNotice',
  async (
    { eprocId, noticeId }: { eprocId: string; noticeId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.analyzeNotice(eprocId, noticeId);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

// Supporting document thunks for prepare response
export const uploadSupportingDocument = createAsyncThunk(
  'tax-portal/uploadSupportingDocument',
  async (
    { eprocId, noticeId, file }: { eprocId: string; noticeId: string; file: File },
    { rejectWithValue },
  ) => {
    try {
      const response = await taxPortalService.parseDocumentForResponse(eprocId, noticeId, file);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const removeSupportingDocument = createAsyncThunk(
  'tax-portal/removeSupportingDocument',
  async (
    { eprocId, noticeId, documentId }: { eprocId: string; noticeId: string; documentId: string },
    { rejectWithValue },
  ) => {
    try {
      await taxPortalService.removeDocumentForResponse(eprocId, noticeId, documentId);
      return { documentId };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const removeAllSupportingDocuments = createAsyncThunk(
  'tax-portal/removeAllSupportingDocuments',
  async (
    { eprocId, noticeId }: { eprocId: string; noticeId: string },
    { rejectWithValue },
  ) => {
    try {
      await taxPortalService.removeAllDocumentsForResponse(eprocId, noticeId);
      return { eprocId, noticeId };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

// Fetch submitted responses for all client e-proceedings (scraped from tax portal)
export const fetchSubmittedResponses = createAsyncThunk(
  'tax-portal/fetchSubmittedResponses',
  async (eprocId: string, { rejectWithValue }) => {
    try {
      const response = await taxPortalService.getSubmittedResponses(eprocId);
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

// What's New feature thunks
export const fetchWhatsNew = createAsyncThunk(
  'tax-portal/fetchWhatsNew',
  async (_, { rejectWithValue }) => {
    try {
      const response = await whatsNewService.getWhatsNew();
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const markRecordAsSeen = createAsyncThunk(
  'tax-portal/markRecordAsSeen',
  async (
    { recordType, recordId }: { recordType: 'returns' | 'proceedings' | 'demands'; recordId: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await whatsNewService.markAsSeen(recordType, recordId);
      // Refresh the whatsNew counts after marking as seen
      dispatch(fetchWhatsNew());
      return response;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

// --- Demand Response Thunks ---

export const generateDemandResponse = createAsyncThunk(
  'taxPortal/generateDemandResponse',
  async ({ demandId, responseCategory, documentIds }: { demandId: string; responseCategory: string; documentIds?: string[] }, { rejectWithValue }) => {
    try {
      return await taxPortalService.generateDemandResponse(demandId, { responseCategory, documentIds });
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const refineDemandResponse = createAsyncThunk(
  'taxPortal/refineDemandResponse',
  async ({ demandId, payload }: { demandId: string; payload: { originalFullResponse: string; selectedText?: string; refinementAction: string; customInstruction?: string; responseCategory?: string; documentIds?: string[] } }, { rejectWithValue }) => {
    try {
      return await taxPortalService.refineDemandResponse(demandId, payload);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

export const uploadDemandSupportingDocument = createAsyncThunk(
  'taxPortal/uploadDemandSupportingDocument',
  async ({ demandId, file }: { demandId: string; file: File }, { rejectWithValue }) => {
    try {
      return await taxPortalService.parseDemandDocumentForResponse(demandId, file);
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  },
);

const taxPortalSlice = createSlice({
  name: 'taxPortal',
  initialState,
  reducers: {
    clearTaxReturnDetails: (state) => {
      state.taxReturns.selectedReturn.data = {} as TaxReturnDetailResponse;
    },
    clearEproceedingDetails: (state) => {
      state.eprocs.selectedEproceeding.data = {} as TaxNoticeDetailResponse;
    },
    clearDemandsDetails: (state) => {
      state.demands.selectedDemand.data = {} as TaxDemandDetailResponse;
    },
    resetSubmitStatus: (state) => {
      state.responses.submitStatus = 'idle';
    },
    setTaxReturnsPage: (state, action: PayloadAction<number>) => {
      state.taxReturns.pagination.page = action.payload;
    },
    setEproceedingsPage: (state, action: PayloadAction<number>) => {
      state.eprocs.listPagination.page = action.payload;
    },
    setCalendarEproceedingsPage: (state, action: PayloadAction<number>) => {
      state.eprocs.calendarPagination.page = action.payload;
    },
    setResponsesPage: (state, action: PayloadAction<number>) => {
      state.responses.pagination.page = action.payload;
    },
    setDemandsPage: (state, action: PayloadAction<number>) => {
      state.demands.pagination.page = action.payload;
    },
    resetNoticeResponseState: (state) => {
      state.noticeResponse = {
        data: null,
        loading: false,
        submitting: false,
        success: false,
        error: null,
      };
    },
    resetGeneratedResponseState: (state) => {
      state.generatedResponse = {
        eprocId: '',
        noticeId: '',
        responseCategory: 'COMPLIANCE',
        responseText: '',
        tokensUsed: 0,
        loading: false,
        submitting: false,
        success: false,
        error: null,
      };
    },
    clearRefinementState: (state) => {
      state.aiRefinement = {
        isRefining: false,
        activeAction: null,
        refinedText: '',
        fullResponse: '',
        tokensUsed: 0,
        error: null,
      };
    },
    setRefinementAction: (state, action: PayloadAction<RefinementAction | null>) => {
      state.aiRefinement.activeAction = action.payload;
    },
    showDetailsModal: (state, action: PayloadAction<string>) => {
      state.showDetailsModal.open = true;
      state.showDetailsModal.name = action.payload;
    },
    hideDetailsModal: (state) => {
      state.showDetailsModal.open = false;
      state.showDetailsModal.name = '';
    },
    clearNoticeAnalysis: (state) => {
      state.noticeAnalysis = {
        data: null,
        noticeId: null,
        loading: false,
        error: null,
        status: null,
        streamingText: '',
        isStreaming: false,
      };
    },
    // Streaming notice analysis actions
    startNoticeAnalysisStream: (state, action: PayloadAction<{ noticeId: string }>) => {
      state.noticeAnalysis = {
        data: null,
        noticeId: action.payload.noticeId,
        loading: true,
        error: null,
        status: null,
        streamingSections: {},
        isStreaming: true,
      };
    },
    setNoticeAnalysisStatus: (state, action: PayloadAction<string>) => {
      state.noticeAnalysis.status = action.payload;
    },
    updateNoticeAnalysisSection: (state, action: PayloadAction<{ name: string; content: unknown }>) => {
      const { name, content } = action.payload;
      // Update the specific section in streamingSections
      (state.noticeAnalysis.streamingSections as Record<string, unknown>)[name] = content;
    },
    completeNoticeAnalysisStream: (state, action: PayloadAction<{ data: NoticeAnalysis; noticeId: string }>) => {
      state.noticeAnalysis.data = action.payload.data;
      state.noticeAnalysis.noticeId = action.payload.noticeId;
      state.noticeAnalysis.loading = false;
      state.noticeAnalysis.isStreaming = false;
      state.noticeAnalysis.status = null;
    },
    setNoticeAnalysisError: (state, action: PayloadAction<string>) => {
      state.noticeAnalysis.loading = false;
      state.noticeAnalysis.isStreaming = false;
      state.noticeAnalysis.error = action.payload;
      state.noticeAnalysis.status = null;
    },
    // Prepare Response actions
    openPrepareResponse: (state, action: PayloadAction<{ eprocId: string; noticeId: string }>) => {
      state.prepareResponse.isOpen = true;
      state.prepareResponse.eprocId = action.payload.eprocId;
      state.prepareResponse.noticeId = action.payload.noticeId;
    },
    closePrepareResponse: (state) => {
      state.prepareResponse.isOpen = false;
      state.prepareResponse.eprocId = null;
      state.prepareResponse.noticeId = null;
      state.prepareResponse.supportingDocuments = [];
      state.prepareResponse.documentUpload = {
        isUploading: false,
        error: null,
      };
    },
    clearSupportingDocuments: (state) => {
      state.prepareResponse.supportingDocuments = [];
    },
    // Demand Prepare Response actions
    openDemandPrepareResponse: (state, action: PayloadAction<{ demandId: string }>) => {
      state.demandPrepareResponse.isOpen = true;
      state.demandPrepareResponse.demandId = action.payload.demandId;
    },
    closeDemandPrepareResponse: (state) => {
      state.demandPrepareResponse.isOpen = false;
      state.demandPrepareResponse.demandId = null;
      state.demandPrepareResponse.supportingDocuments = [];
      state.demandPrepareResponse.documentUpload = { isUploading: false, error: null };
      state.demandGeneratedResponse = { demandId: '', responseCategory: '', responseText: '', tokensUsed: 0, loading: false, error: null };
      state.demandAiRefinement = { isRefining: false, activeAction: null, refinedText: '', fullResponse: '', tokensUsed: 0, error: null };
    },
    removeDemandSupportingDocument: (state, action: PayloadAction<string>) => {
      state.demandPrepareResponse.supportingDocuments = state.demandPrepareResponse.supportingDocuments.filter(d => d.id !== action.payload);
    },
    removeAllDemandSupportingDocuments: (state) => {
      state.demandPrepareResponse.supportingDocuments = [];
    },
    // Submitted Responses Drawer actions
    openSubmittedResponsesDrawer: (state, action: PayloadAction<{ noticeId: string }>) => {
      state.submittedResponses.isOpen = true;
      state.submittedResponses.selectedNoticeId = action.payload.noticeId;
    },
    closeSubmittedResponsesDrawer: (state) => {
      state.submittedResponses.isOpen = false;
      state.submittedResponses.clientId = null;
      state.submittedResponses.clientName = null;
      state.submittedResponses.selectedNoticeId = null;
      state.submittedResponses.data = [];
      state.submittedResponses.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaxPortalOverview.pending, (state) => {
        state.overview.loading = true;
        state.overview.error = null;
      })
      .addCase(
        fetchTaxPortalOverview.fulfilled,
        (state, action: PayloadAction<TaxPortalOverview>) => {
          state.overview.loading = false;
          state.overview.data = {
            ...state.overview.data, // Keep existing data structure
            ...action.payload, // Merge with new data
          };
        },
      )
      .addCase(fetchTaxPortalOverview.rejected, (state, action) => {
        state.overview.loading = false;
        console.log(action);
        state.overview.error =
          (action.payload as { error: string })?.error || 'Failed to fetch tax portal overview';
      });

    builder
      .addCase(fetchTaxReturns.pending, (state) => {
        state.taxReturns.loading = true;
        state.taxReturns.error = null;
      })
      .addCase(fetchTaxReturns.fulfilled, (state, action) => {
        state.taxReturns.loading = false;
        state.taxReturns.list = action.payload.returns;
        state.taxReturns.pagination = action.payload.pagination;
      })
      .addCase(fetchTaxReturns.rejected, (state, action) => {
        state.taxReturns.loading = false;
        state.taxReturns.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch tax returns';
      });

    builder
      .addCase(fetchTaxReturnDetails.pending, (state) => {
        state.taxReturns.selectedReturn.loading = true;
        state.taxReturns.selectedReturn.error = null;
      })
      .addCase(
        fetchTaxReturnDetails.fulfilled,
        (state, action: PayloadAction<TaxReturnDetailResponse>) => {
          state.taxReturns.selectedReturn.loading = false;
          state.taxReturns.selectedReturn.data = action.payload;
        },
      )
      .addCase(fetchTaxReturnDetails.rejected, (state, action) => {
        state.taxReturns.selectedReturn.loading = false;
        state.taxReturns.selectedReturn.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch tax return details';
      });

    builder
      .addCase(fetchTaxEproceedings.pending, (state) => {
        state.eprocs.loading = true;
        state.eprocs.error = null;
      })
      .addCase(fetchTaxEproceedings.fulfilled, (state, action) => {
        state.eprocs.loading = false;
        if (action.payload.view === 'list') {
          state.eprocs.list = action.payload.eprocs;
          state.eprocs.listPagination = action.payload.pagination;
        } else {
          state.eprocs.calendar = action.payload.eprocs;
          state.eprocs.calendarPagination = action.payload.pagination;
        }
      })
      .addCase(fetchTaxEproceedings.rejected, (state, action) => {
        state.eprocs.loading = false;
        state.eprocs.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch tax notices';
      });

    builder
      .addCase(fetchTaxEproceedingDetails.pending, (state) => {
        state.eprocs.selectedEproceeding.loading = true;
        state.eprocs.selectedEproceeding.error = null;
      })
      .addCase(
        fetchTaxEproceedingDetails.fulfilled,
        (state, action: PayloadAction<TaxNoticeDetailResponse>) => {
          state.eprocs.selectedEproceeding.loading = false;
          state.eprocs.selectedEproceeding.data = action.payload;
          state.eprocs.selectedEproceeding.error = null;
        },
      )
      .addCase(fetchTaxEproceedingDetails.rejected, (state, action) => {
        state.eprocs.selectedEproceeding.loading = false;
        state.eprocs.selectedEproceeding.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch Eproceeding details';
      });

    builder
      .addCase(fetchPendingResponses.pending, (state) => {
        state.responses.loading = true;
        state.responses.error = null;
      })
      .addCase(fetchPendingResponses.fulfilled, (state, action) => {
        state.responses.loading = false;
        state.responses.pendingList = action.payload.responses;
        state.responses.pagination = action.payload.pagination;
      })
      .addCase(fetchPendingResponses.rejected, (state, action) => {
        state.responses.loading = false;
        state.responses.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch pending responses';
      });

    builder
      .addCase(fetchResponseHistory.pending, (state) => {
        state.responses.loading = true;
        state.responses.error = null;
      })
      .addCase(fetchResponseHistory.fulfilled, (state, action) => {
        state.responses.loading = false;
        state.responses.history = action.payload.responses;
        state.responses.pagination = {
          page: action.payload.pagination.page,
          limit: action.payload.pagination.limit,
          total: action.payload.pagination.total,
          totalPages: action.payload.pagination.pages, // Map 'pages' to 'totalPages'
        };
      })
      .addCase(fetchResponseHistory.rejected, (state, action) => {
        state.responses.loading = false;
        state.responses.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch response history';
      });

    builder
      .addCase(submitResponse.pending, (state) => {
        state.responses.submitStatus = 'loading';
        state.responses.error = null;
      })
      .addCase(submitResponse.fulfilled, (state) => {
        state.responses.submitStatus = 'succeeded';
      })
      .addCase(submitResponse.rejected, (state, action) => {
        state.responses.submitStatus = 'failed';
        state.responses.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to submit response';
      });

    builder
      .addCase(fetchTaxDemands.pending, (state) => {
        state.demands.loading = true;
        state.demands.error = null;
      })
      .addCase(fetchTaxDemands.fulfilled, (state, action) => {
        state.demands.loading = false;
        state.demands.list = action.payload.demands;
        state.demands.pagination = action.payload.pagination;
      })
      .addCase(fetchTaxDemands.rejected, (state, action) => {
        state.demands.loading = false;
        state.demands.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch tax demands';
      });

    builder
      .addCase(fetchTaxDemandsDetails.pending, (state) => {
        state.demands.selectedDemand.loading = true;
        state.demands.selectedDemand.error = null;
      })
      .addCase(fetchTaxDemandsDetails.fulfilled, (state, action) => {
        state.demands.selectedDemand.loading = false;
        state.demands.selectedDemand.data = action.payload || null;
      })
      .addCase(fetchTaxDemandsDetails.rejected, (state, action) => {
        state.demands.selectedDemand.loading = false;
        state.demands.selectedDemand.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch tax demands details';
      });

    // Notice Response
    builder
      .addCase(fetchNoticeResponse.pending, (state) => {
        state.noticeResponse.loading = true;
        state.noticeResponse.error = null;
      })
      .addCase(fetchNoticeResponse.fulfilled, (state, action) => {
        state.noticeResponse.loading = false;
        state.noticeResponse.data = action.payload.data || null;
      })
      .addCase(fetchNoticeResponse.rejected, (state, action) => {
        state.noticeResponse.loading = false;
        state.noticeResponse.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch notice response';
      })
      // Generate Notice Response
      .addCase(generateNoticeResponse.pending, (state) => {
        state.generatedResponse.loading = true;
        state.generatedResponse.error = null;
      })
      .addCase(generateNoticeResponse.fulfilled, (state, action) => {
        state.generatedResponse.loading = false;
        state.generatedResponse.eprocId = action.payload.eprocId;
        state.generatedResponse.noticeId = action.payload.noticeId;
        state.generatedResponse.responseCategory = action.payload.responseCategory;
        state.generatedResponse.responseText = action.payload.responseText;
        state.generatedResponse.tokensUsed = action.payload.tokensUsed;
        state.generatedResponse.success = true;
      })
      .addCase(generateNoticeResponse.rejected, (state, action) => {
        state.generatedResponse.loading = false;
        state.generatedResponse.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to generate response';
      })
      // Submit Notice Response
      .addCase(submitNoticeResponse.pending, (state) => {
        state.noticeResponse.submitting = true;
        state.noticeResponse.success = false;
        state.noticeResponse.error = null;
      })
      .addCase(submitNoticeResponse.fulfilled, (state) => {
        state.noticeResponse.submitting = false;
        state.noticeResponse.success = true;
      })
      .addCase(submitNoticeResponse.rejected, (state, action) => {
        state.noticeResponse.submitting = false;
        state.noticeResponse.success = false;
        state.noticeResponse.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to submit response';
      })
      // Refine Notice Response
      .addCase(refineNoticeResponse.pending, (state) => {
        state.aiRefinement.isRefining = true;
        state.aiRefinement.error = null;
      })
      .addCase(refineNoticeResponse.fulfilled, (state, action) => {
        state.aiRefinement.isRefining = false;
        state.aiRefinement.refinedText = action.payload.refinedText;
        state.aiRefinement.fullResponse = action.payload.fullResponse;
        state.aiRefinement.tokensUsed = action.payload.tokensUsed;
        state.aiRefinement.activeAction = null;
        // Also update the generated response text with the refined version
        state.generatedResponse.responseText = action.payload.fullResponse;
      })
      .addCase(refineNoticeResponse.rejected, (state, action) => {
        state.aiRefinement.isRefining = false;
        state.aiRefinement.activeAction = null;
        state.aiRefinement.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to refine response';
      })
      // Analyze Notice
      .addCase(analyzeNotice.pending, (state, action) => {
        state.noticeAnalysis.loading = true;
        state.noticeAnalysis.error = null;
        state.noticeAnalysis.noticeId = action.meta.arg.noticeId;
      })
      .addCase(analyzeNotice.fulfilled, (state, action) => {
        state.noticeAnalysis.loading = false;
        state.noticeAnalysis.data = action.payload.analysis;
        state.noticeAnalysis.noticeId = action.payload.noticeId;
      })
      .addCase(analyzeNotice.rejected, (state, action) => {
        state.noticeAnalysis.loading = false;
        state.noticeAnalysis.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to analyze notice';
      })
      // Upload Supporting Document
      .addCase(uploadSupportingDocument.pending, (state) => {
        state.prepareResponse.documentUpload.isUploading = true;
        state.prepareResponse.documentUpload.error = null;
      })
      .addCase(uploadSupportingDocument.fulfilled, (state, action) => {
        state.prepareResponse.documentUpload.isUploading = false;
        const docData = action.payload.data;
        state.prepareResponse.supportingDocuments.push({
          id: docData.documentId,
          fileName: docData.fileName,
          fileType: docData.fileType,
          characterCount: docData.characterCount,
        });
      })
      .addCase(uploadSupportingDocument.rejected, (state, action) => {
        state.prepareResponse.documentUpload.isUploading = false;
        state.prepareResponse.documentUpload.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to upload document';
      })
      // Remove Supporting Document
      .addCase(removeSupportingDocument.fulfilled, (state, action) => {
        state.prepareResponse.supportingDocuments = state.prepareResponse.supportingDocuments.filter(
          (doc) => doc.id !== action.payload.documentId
        );
      })
      // Remove All Supporting Documents
      .addCase(removeAllSupportingDocuments.fulfilled, (state) => {
        state.prepareResponse.supportingDocuments = [];
      })
      // Fetch Submitted Responses
      .addCase(fetchSubmittedResponses.pending, (state) => {
        state.submittedResponses.loading = true;
        state.submittedResponses.error = null;
      })
      .addCase(fetchSubmittedResponses.fulfilled, (state, action) => {
        state.submittedResponses.loading = false;
        state.submittedResponses.data = action.payload.responses;
        state.submittedResponses.clientId = action.payload.clientId;
        state.submittedResponses.clientName = action.payload.clientName;
      })
      .addCase(fetchSubmittedResponses.rejected, (state, action) => {
        state.submittedResponses.loading = false;
        state.submittedResponses.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch submitted responses';
      })
      // What's New
      .addCase(fetchWhatsNew.pending, (state) => {
        state.whatsNew.loading = true;
        state.whatsNew.error = null;
      })
      .addCase(fetchWhatsNew.fulfilled, (state, action) => {
        state.whatsNew.loading = false;
        state.whatsNew.data = action.payload;
      })
      .addCase(fetchWhatsNew.rejected, (state, action) => {
        state.whatsNew.loading = false;
        state.whatsNew.error = action.payload
          ? (action.payload as { error: string }).error
          : 'Failed to fetch what\'s new';
      })
      // Demand Response
      .addCase(generateDemandResponse.pending, (state) => {
        state.demandGeneratedResponse.loading = true;
        state.demandGeneratedResponse.error = null;
      })
      .addCase(generateDemandResponse.fulfilled, (state, action) => {
        state.demandGeneratedResponse.loading = false;
        state.demandGeneratedResponse.demandId = action.payload.demandId;
        state.demandGeneratedResponse.responseCategory = action.payload.responseCategory;
        state.demandGeneratedResponse.responseText = action.payload.responseText;
        state.demandGeneratedResponse.tokensUsed = action.payload.tokensUsed;
      })
      .addCase(generateDemandResponse.rejected, (state, action) => {
        state.demandGeneratedResponse.loading = false;
        state.demandGeneratedResponse.error = action.payload ? (action.payload as { error: string }).error : 'Failed to generate response';
      })
      .addCase(refineDemandResponse.pending, (state) => {
        state.demandAiRefinement.isRefining = true;
        state.demandAiRefinement.error = null;
      })
      .addCase(refineDemandResponse.fulfilled, (state, action) => {
        state.demandAiRefinement.isRefining = false;
        state.demandAiRefinement.refinedText = action.payload.refinedText;
        state.demandAiRefinement.fullResponse = action.payload.fullResponse;
        state.demandAiRefinement.tokensUsed = action.payload.tokensUsed;
      })
      .addCase(refineDemandResponse.rejected, (state, action) => {
        state.demandAiRefinement.isRefining = false;
        state.demandAiRefinement.error = action.payload ? (action.payload as { error: string }).error : 'Failed to refine response';
      })
      .addCase(uploadDemandSupportingDocument.pending, (state) => {
        state.demandPrepareResponse.documentUpload = { isUploading: true, error: null };
      })
      .addCase(uploadDemandSupportingDocument.fulfilled, (state, action) => {
        state.demandPrepareResponse.documentUpload = { isUploading: false, error: null };
        state.demandPrepareResponse.supportingDocuments.push({
          id: action.payload.documentId,
          fileName: action.payload.fileName,
          fileType: action.payload.fileType,
          characterCount: action.payload.characterCount,
        });
      })
      .addCase(uploadDemandSupportingDocument.rejected, (state, action) => {
        state.demandPrepareResponse.documentUpload = { isUploading: false, error: action.payload ? (action.payload as { error: string }).error : 'Upload failed' };
      });
  },
});

export const {
  clearTaxReturnDetails,
  clearEproceedingDetails,
  clearDemandsDetails,
  resetSubmitStatus,
  setTaxReturnsPage,
  setEproceedingsPage,
  setCalendarEproceedingsPage,
  setResponsesPage,
  setDemandsPage,
  resetNoticeResponseState,
  resetGeneratedResponseState,
  clearRefinementState,
  setRefinementAction,
  showDetailsModal,
  hideDetailsModal,
  clearNoticeAnalysis,
  startNoticeAnalysisStream,
  setNoticeAnalysisStatus,
  updateNoticeAnalysisSection,
  completeNoticeAnalysisStream,
  setNoticeAnalysisError,
  openPrepareResponse,
  closePrepareResponse,
  clearSupportingDocuments,
  openDemandPrepareResponse,
  closeDemandPrepareResponse,
  removeDemandSupportingDocument,
  removeAllDemandSupportingDocuments,
  openSubmittedResponsesDrawer,
  closeSubmittedResponsesDrawer,
} = taxPortalSlice.actions;

export default taxPortalSlice.reducer;
